import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ClickAwayListener, MenuList, MenuItem, Paper, Popper, Box } from '@mui/material';
import styles from './styles.module.scss';
import { ActionDropdownProps, ActionItemOption } from './types';

/**
 *
 * @param {ActionDropdownProps} props - list out the props.
 * @param {boolean} props.open - opens the dropdown menu
 * @param {Partial<Modifier<any, any>>[]} props.popperModifiers - adjusts the position of dropdown menu relative to anchor as per need
 * @param {boolean} props.onToggle - callback function that changes open state of the dropdown menu
 * @param {Option[]} props.options - list of options for the dropdown menu which includes label, value, icon and the event to trigger when clicked
 * @param {JSX.Element} props.trigger - the anchor element which when clicked would toggle the dropdown menu
 * @returns {ActionDropdown} Rendered Action Menu Dropdown component.
 */

/**
 * Action Dropdown component
 *
 * This is the Action Menu Dropdown component written using storybook. It handles different hover, focused and active states.
 */

export const ActionDropdown: React.FC<ActionDropdownProps> = memo(
  ({
    anchorEl,
    open,
    isOptionALink,
    popperModifiers = [
      {
        name: 'offset',
        options: {
          offset: [0, 0]
        }
      }
    ],
    onToggle,
    options,
    trigger,
    className
  }) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [isMounted, setIsMounted] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(0);

    if (!options || options.length === 0) return null;

    const handleClose = useCallback(() => {
      setFocusedIndex(0);
      onToggle(false);
    }, [onToggle]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setFocusedIndex((prev) => (prev + 1) % options.length);
            break;
          case 'ArrowUp':
            e.preventDefault();
            setFocusedIndex((prev) => (prev - 1 + options.length) % options.length);
            break;
          case 'Enter':
          case ' ':
            e.preventDefault();
            options[focusedIndex].onClick(e as unknown as React.MouseEvent<HTMLElement>);
            handleClose();
            break;
          case 'Escape':
            e.preventDefault();
            handleClose();
            break;
          default:
            break;
        }
      },
      [focusedIndex, options, handleClose]
    );

    const handleItemClick = useCallback(
      (option: ActionItemOption, e: React.MouseEvent<HTMLElement>) => {
        option.onClick(e);
        handleClose();

        if (anchorEl instanceof HTMLElement) {
          setTimeout(() => anchorEl.focus(), 0);
        }
      },
      [handleClose, anchorEl]
    );

    useEffect(() => {
      if (open && menuRef.current) {
        const firstItem = menuRef.current.querySelector('li') as HTMLLIElement;
        if (firstItem) {
          firstItem.focus();
        }
      }
    }, [open]);

    useEffect(() => {
      if (anchorRef.current) {
        setIsMounted(true);
      }
    }, []);

    return (
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          <Box
            onClick={() => onToggle(!open)}
            ref={anchorRef}
            aria-haspopup="true"
            onKeyDown={handleKeyDown}
          >
            {trigger}
          </Box>
          {isMounted && (
            <Popper
              open={open}
              anchorEl={anchorEl ?? anchorRef.current}
              placement="bottom-start"
              className={`${styles.actionDropdownBody} ${className}`}
              modifiers={popperModifiers}
              data-testid="action-menu-list"
              disablePortal
            >
              <Paper className={styles.actionMenuPaper}>
                <MenuList className={styles.actionMenuList} ref={menuRef}>
                  {options.map((option, index) => (
                    <MenuItem
                      autoFocus={index === focusedIndex}
                      className={`${styles.actionMenuItem} ${
                        isOptionALink ? styles.actionMenuItemLink : ''
                      }`}
                      key={option.value}
                      tabIndex={0}
                      data-testid="action-menu-item"
                      onClick={(e: React.MouseEvent<HTMLElement>) => handleItemClick(option, e)}
                      onFocus={() => setFocusedIndex(index)}
                      disableRipple
                    >
                      {option.icon && (
                        <span style={option.customStyles} className={styles.actionMenuListItemIcon}>
                          {option.icon}
                        </span>
                      )}
                      <span style={option.customStyles}>{option.label}</span>
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            </Popper>
          )}
        </div>
      </ClickAwayListener>
    );
  }
);
